import React, { useState, useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react";
import { Modal, TextField, Typography, Select, Autocomplete } from "@ann/web-components";
import { useMediaQueries } from "../../../../../../../utils/mediaQueries";
import { useToast } from "../../../../../../../components/toast";
import { useStore } from "../../../../../../../hooks";
import { createKnowledge } from "../../../../services/knowledge";
import {
  ModalBodyContent,
  FieldContainer,
  ErrorMessage,
} from "./CreateKnowledgeModal.styled";

/**
 * No-operation function (noop) - a stable empty function reference.
 * Used as a placeholder for required callback props that don't need implementation.
 * Defined outside the component to maintain referential equality across renders,
 * avoiding unnecessary re-renders of child components.
 */
const noop = () => {};

const CreateKnowledgeModal = observer(({
  isOpen,
  onClose,
  size = "medium",
  onSuccess,
  departmentsList = [],
}) => {
  const { isMobile } = useMediaQueries();
  const toast = useToast();
  const { usersStore } = useStore();

  const [knowledgeBaseName, setKnowledgeBaseName] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerInputValue, setOwnerInputValue] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  // Calculate modal width: 90% for mobile, 768px otherwise
  const modalWidth = useMemo(() => {
    if (isMobile) {
      return "90%";
    }
    return "768px";
  }, [isMobile]);

  // Transform departmentsList to format expected by Select component
  const departments = useMemo(() => {
    return departmentsList.map((dept) => ({
      department_uuid: dept.departmentUuid || dept.uuid,
      departmentUuid: dept.departmentUuid || dept.uuid,
      department_name: dept.departmentName || dept.name,
      departmentName: dept.departmentName || dept.name,
      name: dept.departmentName || dept.name,
    }));
  }, [departmentsList]);

  const loadingDepartments = false; // No longer loading since we receive it as prop

  // Load users when modal opens
  useEffect(() => {
    if (!isOpen) return;
    
    // Only load if store is empty (users not loaded yet)
    if (usersStore.users.length === 0) {
      usersStore.getUserList({
        page: 1,
        page_size: 100, // Get all users
        order_by: "fullName",
        order_direction: "ASC",
      }).catch((error) => {
        console.error('Error loading users from UsersStore:', error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, usersStore.users.length]);

  // Format users from store for Autocomplete
  const usersList = useMemo(() => {
    if (!usersStore.users || !Array.isArray(usersStore.users)) {
      return [];
    }
    return usersStore.users.map((user) => ({
      user_uuid: user.user_uuid || user.userUuid || user.uuid,
      full_name: user.fullName || user.full_name || "Unknown",
      email: user.email || "",
    }));
  }, [usersStore.users]);

  // Simple setters don't need useCallback - useState setters are stable
  const handleKnowledgeBaseNameChange = (value) => {
    setKnowledgeBaseName(value);
  };

  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
  };

  const handleOwnerInputChange = useCallback((value) => {
    // onChange - user typing in the input
    setOwnerInputValue(value);
    // Reset selection when user types
    if (!value) {
      setSelectedOwner(null);
    }
  }, []);

  const handleOwnerSelect = useCallback((suggestion) => {
    // onSuggestionSelect - user selected a suggestion from the list
    if (suggestion) {
      const uuid = suggestion.user_uuid;
      const name = suggestion.full_name;
      setSelectedOwner(uuid);
      setOwnerInputValue(name);
    }
  }, []);

  const handleClose = useCallback(() => {
    // Reset form state
    setKnowledgeBaseName("");
    setSelectedDepartment(null);
    setSelectedOwner(null);
    setOwnerInputValue("");
    onClose();
  }, [onClose]);

  // Field validation states
  const isNameEmpty = !knowledgeBaseName.trim();
  const isDepartmentEmpty = !selectedDepartment;
  // const isOwnerEmpty = !selectedOwner; // Temporarily disabled

  const isFormValid = useMemo(() => {
    return !isNameEmpty && !isDepartmentEmpty;
  }, [isNameEmpty, isDepartmentEmpty]);

  const handleCreate = useCallback(async () => {
    if (!isFormValid) {
      return;
    }

    const trimmedName = knowledgeBaseName.trim();
    setIsCreating(true);
    
    try {
      await createKnowledge({
        name: trimmedName,
        department_uuid: selectedDepartment,
      });

      // Success
      toast.success({
        message: `${trimmedName} was created successfully`,
        canClose: true,
      });

      // Reset form and close modal
      setKnowledgeBaseName("");
      setSelectedDepartment(null);
      setSelectedOwner(null);
      setOwnerInputValue("");
      onClose();

      // Refresh knowledge bases list
      onSuccess?.();
    } catch (error) {
      // Error handling
      const errorMessage =
        error.message || "An error occurred while creating knowledge base";
      toast.error({
        message: errorMessage,
        canClose: true,
      });
    } finally {
      setIsCreating(false);
    }
  }, [knowledgeBaseName, selectedDepartment, selectedOwner, isFormValid, toast, onClose, onSuccess]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size={size}>
      <Modal.Header>
        <Modal.Header.Title>Create New Knowledge Base</Modal.Header.Title>
        <Modal.Header.Description>
          <Typography category="body" size="medium" weight="regular" color="body">
            Set up a new knowledge base for your organization. You can add KBUs after creation.
          </Typography>
        </Modal.Header.Description>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <FieldContainer>
            <Typography category="subtitle" size="medium" weight="bold">
              Knowledge Base Name
            </Typography>
            <TextField
              placeholder="Enter Knowledge base name"
              size="small"
              value={knowledgeBaseName}
              onChange={handleKnowledgeBaseNameChange}
            />
            {isNameEmpty && (
              <ErrorMessage>Knowledge base name is required</ErrorMessage>
            )}
          </FieldContainer>

          <FieldContainer>
            <Typography category="subtitle" size="medium" weight="bold">
              Departments
            </Typography>
            <Select
              onChange={handleDepartmentChange}
              onSelectionChange={handleDepartmentChange}
              size="small"
              width="100%"
              placeholder="Select a department"
              disabled={loadingDepartments}
            >
              <Select.Menu>
                {departments.map((dept) => {
                  // Defensive programming: handle both camelCase and snake_case
                  const departmentUuid = dept.department_uuid || dept.departmentUuid;
                  const departmentName = dept.department_name || dept.departmentName || dept.name || "Unknown";
                  
                  return (
                    <Select.Item
                      key={departmentUuid}
                      label={departmentName}
                      value={departmentUuid}
                    >
                      {departmentName}
                    </Select.Item>
                  );
                })}
              </Select.Menu>
            </Select>
            {isDepartmentEmpty && (
              <ErrorMessage>Department is required</ErrorMessage>
            )}
          </FieldContainer>

          {/* Owner field - temporarily disabled
          <FieldContainer className="owner-field">
            <Typography category="subtitle" size="medium" weight="bold">
              Owner
            </Typography>
            <Autocomplete
              placeholder="Search and select owner"
              size="small"
              value={ownerInputValue}
              onChange={handleOwnerInputChange}
              onSuggestionSelect={handleOwnerSelect}
              onSearch={noop}
              suggestions={usersList}
              valueKey="user_uuid"
              labelKey="full_name"
            />
            {isOwnerEmpty && (
              <ErrorMessage>Owner is required</ErrorMessage>
            )}
          </FieldContainer>
          */}
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isCreating}
        />
        <Modal.Button
          label={isCreating ? "Creating..." : "Create Knowledge Base"}
          onClick={handleCreate}
          disabled={!isFormValid || isCreating}
        />
      </Modal.Actions>
    </Modal>
  );
});

export default CreateKnowledgeModal;

